// Stripe Payment Form Component
import React, { useState, useEffect } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { AlertCircle, Check, Loader } from 'lucide-react';

const StripePaymentForm = ({ amount, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentReady, setPaymentReady] = useState(false);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setErrorMessage('');

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/enrollment/success',
        },
        redirect: 'if_required', // Only redirect if 3D Secure is required
      });

      if (error) {
        setErrorMessage(error.message);
        setProcessing(false);
        if (onError) onError(error);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Payment successful
        if (onSuccess) {
          onSuccess(paymentIntent);
        }
      }
    } catch (err) {
      setErrorMessage('An unexpected error occurred. Please try again.');
      setProcessing(false);
      if (onError) onError(err);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Amount Display */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Amount to Pay</span>
          <span className="text-2xl font-bold text-gray-900">{formatPrice(amount)}</span>
        </div>
      </div>

      {/* Stripe Payment Element */}
      <div className="border border-gray-300 rounded-lg p-4">
        <PaymentElement
          onReady={() => setPaymentReady(true)}
          options={{
            layout: 'tabs',
            defaultValues: {
              billingDetails: {
                // Can pre-fill with student/parent info if needed
              }
            }
          }}
        />
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-700 text-sm">{errorMessage}</span>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={!stripe || !paymentReady || processing}
        className={`w-full flex items-center justify-center px-6 py-3 text-base font-medium text-white rounded-lg transition-colors ${
          !stripe || !paymentReady || processing
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-blue-600 hover:bg-blue-700'
        }`}
      >
        {processing ? (
          <>
            <Loader className="w-5 h-5 mr-2 animate-spin" />
            Processing Payment...
          </>
        ) : (
          <>
            <Check className="w-5 h-5 mr-2" />
            Pay {formatPrice(amount)}
          </>
        )}
      </button>

      {/* Security Notice */}
      <p className="text-xs text-center text-gray-500">
        Your payment information is secure and encrypted. We never store your card details.
      </p>
    </form>
  );
};

export default StripePaymentForm;
